import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaFileCsv, FaBookOpen } from 'react-icons/fa';
import VisitationList from '../components/visitations/VisitationList';
import VisitationForm from '../components/visitations/VisitationForm';
import { Modal } from '../components/common/Modal'; // Changed to named import
import type { Visitation } from '../types/visitation';
import { visitationService } from '../services/visitation.service';
import { useNotification } from '../contexts/NotificationContext';

const VisitationPage: React.FC = () => {
  const [visitations, setVisitations] = useState<Visitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVisitation, setSelectedVisitation] = useState<Visitation | null>(null);
  const { addNotification } = useNotification();

  const fetchVisitations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await visitationService.getAllVisitations();
      setVisitations(data);
    } catch (err) {
      setError('심방 기록을 불러오는 데 실패했습니다.');
      addNotification('심방 기록 로딩 실패', 'error');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchVisitations();
  }, [fetchVisitations]);

  const handleAddVisitation = () => {
    setSelectedVisitation(null);
    setIsModalOpen(true);
  };

  const handleEditVisitation = (visitation: Visitation) => {
    setSelectedVisitation(visitation);
    setIsModalOpen(true);
  };

  const handleDeleteVisitation = async (id: string) => {
    if (window.confirm('정말로 이 심방 기록을 삭제하시겠습니까?')) {
      try {
        await visitationService.deleteVisitation(id);
        addNotification('심방 기록이 삭제되었습니다.', 'success');
        fetchVisitations(); // Refresh list
      } catch (err) {
        addNotification('심방 기록 삭제 실패', 'error');
        console.error(err);
      }
    }
  };

  const handleFormSuccess = () => {
    setIsModalOpen(false);
    fetchVisitations(); // Refresh list
  };

  const handleExportCsv = () => {
    if (visitations.length === 0) {
      addNotification('내보낼 데이터가 없습니다.', 'info');
      return;
    }

    const headers = ['심방 날짜', '대상자', '방문자', '유형', '상태', '내용'];
    
    const escapeCSV = (str: string | undefined | null) => {
      if (str === null || str === undefined) {
        return '""';
      }
      const s = String(str);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const data = visitations.map(v => [
      v.visitDate ? new Date(v.visitDate.toDate()).toLocaleDateString('ko-KR') : '',
      escapeCSV(v.memberName),
      escapeCSV(Array.isArray(v.visitors) ? v.visitors.join('; ') : v.visitors),
      escapeCSV(v.type),
      escapeCSV(v.status),
      escapeCSV(v.notes)
    ].join(','));

    const csvContent = ['\uFEFF' + headers.join(','), ...data].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', '심방_목록.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addNotification('CSV 파일이 다운로드되었습니다.', 'success');
  };

  return (
    <div className="p-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <div className="flex items-center">
          <FaBookOpen className="text-3xl text-gray-600 dark:text-gray-200 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-600 dark:text-gray-200">심방 관리</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">교인 심방 기록을 관리하고 추적합니다.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleExportCsv}
            className="bg-btn-blue-bg hover:bg-btn-blue-hover text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out flex items-center justify-center whitespace-nowrap"
          >
            <FaFileCsv className="mr-2" /> CSV 내보내기
          </button>
          <button
            onClick={handleAddVisitation}
            className="bg-btn-green-bg hover:bg-btn-green-hover text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out flex items-center justify-center whitespace-nowrap"
          >
            <FaPlus className="mr-2" /> 새 심방 기록
          </button>
        </div>
      </div>
    
      <div className="mt-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
          {isLoading && <div className="text-center py-4">심방 기록을 불러오는 중입니다...</div>}
          {error && <p className="text-red-500 text-center py-4">{error}</p>}
          {!isLoading && !error && (
            <VisitationList
              visitations={visitations}
              onEdit={handleEditVisitation}
              onDelete={handleDeleteVisitation}
              isLoading={isLoading}
            />
          )}
          {!isLoading && !error && visitations.length === 0 && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">등록된 심방 기록이 없습니다. "새 심방 기록" 버튼을 클릭하여 추가해주세요.</p>
          )}
        </div>
      </div>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedVisitation ? "심방 기록 수정" : "새 심방 기록 추가"}>
          <VisitationForm
            visitation={selectedVisitation}
            onSuccess={handleFormSuccess}
            onCancel={() => setIsModalOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default VisitationPage;
